import {
  Button,
  Checkbox,
  Icon,
  useCallbackRef,
  useUtilities,
} from '@faxi/web-component-library';
import {
  FormField,
  FormRef,
  validationRegexes,
  validators,
} from '@faxi/web-form';
import { useMutation } from '@tanstack/react-query';
import classNames from 'classnames';
import { InfoModal, InputField } from 'components';
import env from 'env';
import parse from 'html-react-parser';
import { FindDealerFormFields, StorageApplicationFormType } from 'models';
import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import storageService from 'services';

import { InfoModalRef } from '../../../../components/_molecules/InfoModal/InfoModal.component';
import { defaultMutationFn } from '../../../../config/api/queryClient';
import { breakTranslationNewlines } from '../../../../utils';
import validation from '../../../../validation';
import LegalNoteModal from '../ValueOfYourCarForm/components/LegalNoteModal.component';
import PrivacyPolicyModal from '../ValueOfYourCarForm/components/PrivacyPolicyModal.component';
import { StyledFindDealerForm } from './FindDealerForm.styled';

export type FindDealerFormProps = {
  changeStep: Dispatch<SetStateAction<number>>;
};

const FindDealerForm: FC<FindDealerFormProps> = props => {
  const { changeStep } = props;

  const { t } = useTranslation();

  const [initialData, setInitialData] = useState<FindDealerFormFields | {}>();
  const [isPrivacyPolicyAccepted, setIsPrivacyPolicyAccepted] = useState(false);
  const infoModalRef = useRef<InfoModalRef>(null);
  const [form, formRef] = useCallbackRef<FormRef>();

  const { prompts, showOverlay, hideOverlay } = useUtilities();

  const { mutateAsync } = useMutation<any, any, any>({
    mutationKey: ['save-offer'],
    mutationFn: v => defaultMutationFn('save-offer', v),
    onSuccess: () => {
      prompts.infoSuccess({
        title: t('ced-application-tab3-success-title'),
        confirmButtonText: t('ced-action-close'),
        content: t('ced-application-tab3-success-description'),
        position: 'center',
      });

      storageService.removeItem(env.VITE_FORM_STORAGE_KEY);
      changeStep(1);
    },
  });

  const handleSubmit = useCallback(
    async (values: FindDealerFormFields) => {
      try {
        showOverlay('.cedolauto-application');
        Object.keys(values).forEach(key => {
          storageService.updateItem(
            key,
            values?.[key as keyof FindDealerFormFields]
          );
        });

        const quotationID =
          +(
            storageService.getItem(
              env.VITE_FORM_STORAGE_KEY
            ) as StorageApplicationFormType
          )?.quotationID || 0;

        await mutateAsync({ ...values, quotationID });
        infoModalRef.current?.open();
      } catch (e) {
        console.error(e);
      } finally {
        hideOverlay('.cedolauto-application');
      }
    },
    [hideOverlay, mutateAsync, showOverlay]
  );

  const validations = useMemo(
    () => ({
      firstName: [
        validators.general.required(
          t('ced-application-validation-name_required', {
            name: t('ced-application-tab3-first-name'),
          })
        ),
        validators.general.regex(
          validationRegexes.name,
          t('ced-application-validation-name_valid', {
            name: t('ced-application-tab3-first-name').toLowerCase(),
          })
        ),
        validators.general.maxLength(
          60,
          t('ced-application-validation-length', { length: 60 })
        ),
      ],
      lastName: [
        validators.general.required(
          t('ced-application-validation-name_required', {
            name: t('ced-application-tab3-last-name'),
          })
        ),
        validators.general.regex(
          validationRegexes.name,
          t('ced-application-validation-name_valid', {
            name: t('ced-application-tab3-last-name').toLowerCase(),
          })
        ),
        validators.general.maxLength(
          60,
          t('ced-application-validation-length', { length: 60 })
        ),
      ],
      email: [
        validators.general.required(
          t('ced-application-validation-name_required', {
            name: t('ced-application-tab3-email'),
          })
        ),
        validators.general.regex(
          validationRegexes.workEmail,
          t('ced-application-validation-name_valid', {
            name: t('ced-application-tab3-email').toLowerCase(),
          })
        ),
        validators.general.maxLength(
          80,
          t('ced-application-validation-length', { length: 80 })
        ),
      ],
      phoneNumber: [
        validators.general.required(
          t('ced-application-validation-name_required', {
            name: t('section_5-step_3-field_2'),
          })
        ),
        validators.phone.regex(
          validation.PHONE_NUMBER,
          t('ced-application-validation-name_valid', {
            name: t('section_5-step_3-field_2').toLowerCase(),
          })
        ),
      ],
      zipCode: [
        validators.general.required(
          t('ced-application-validation-name_required', {
            name: t('section_5-step_3-field_4'),
          })
        ),
        validators.phone.regex(
          validation.ZIP_CODE,
          t('ced-application-validation-name_valid', {
            name: t('section_5-step_3-field_4').toLowerCase(),
          })
        ),
      ],
    }),
    [t]
  );

  useEffect(() => {
    setInitialData(storageService.getItem(env.VITE_FORM_STORAGE_KEY) || {});
  }, []);

  return (
    <StyledFindDealerForm
      className={classNames(
        'cedolauto-find-dealer-form',
        'cedolauto-application__form'
      )}
      onSubmit={handleSubmit}
      initialData={initialData}
      ref={formRef}
    >
      <div className="cedolauto-application__form__wrapper">
        <div className="cedolauto-application__form__wrapper__fields">
          <FormField
            component={InputField}
            name="firstName"
            label={t('ced-application-tab3-first-name')}
            validate={validations.firstName}
          />

          <FormField
            component={InputField}
            name="lastName"
            label={t('ced-application-tab3-last-name')}
            validate={validations.lastName}
          />

          <FormField
            component={InputField}
            name="email"
            label={t('ced-application-tab3-email')}
            validate={validations.email}
          />

          <FormField
            component={InputField}
            name="zipCode"
            label={t('section_5-step_3-field_4')}
            validate={validations.zipCode}
          />

          <FormField
            component={InputField}
            name="phoneNumber"
            label={t('section_5-step_3-field_2')}
            validate={validations.phoneNumber}
          />

          <LegalNoteModal />
          <div className="cedolauto-find-dealer-form__privacy-policy">
            <Checkbox
              checked={isPrivacyPolicyAccepted}
              onChange={value => {
                setIsPrivacyPolicyAccepted(value);
              }}
            />
            <div className="cedolauto-find-dealer-form__privacy-policy__checkbox-label">
              {parse(
                breakTranslationNewlines(
                  t('section_5-step_3-disclaimer-checkbox')
                )
              )}
              <PrivacyPolicyModal />
            </div>
          </div>
        </div>

        <div className="cedolauto-find-dealer-form__desc">
          <p className="cedolauto-find-dealer-form__desc__title">
            {parse(
              breakTranslationNewlines(t('ced-application-tab3-info-title'))
            )}
          </p>
          <p className="cedolauto-find-dealer-form__desc__content">
            {parse(
              breakTranslationNewlines(t('ced-application-tab3-info-copy'))
            )}
          </p>
        </div>
      </div>

      <div className="cedolauto-find-dealer-form__actions">
        <Button
          variant="ghost"
          className="cedolauto-find-dealer-form__actions__prev-step"
          onClick={() => {
            changeStep(old => old - 1);
          }}
          icon={<Icon name="chevron-down" />}
        >
          {t('ced-action-previous-step')}
        </Button>
        <Button
          className="cedolauto-application__form__submit"
          type="submit"
          disabled={
            !form?.isFormChanged() ||
            !form?.syncFormValid ||
            !isPrivacyPolicyAccepted
          }
        >
          {t('ced-action-submit')}
        </Button>

        <InfoModal
          ref={infoModalRef}
          className="cedolauto-find-dealer-form__successful-submission-modal"
          title={t('ced-application-tab3-success-title')}
        >
          <p>{t('ced-form-sent-confirmation-text')}</p>
        </InfoModal>
      </div>
    </StyledFindDealerForm>
  );
};

export default FindDealerForm;
